// Draw only when the picture can have changed.
//
// The render loop drew every frame whether or not anything had moved.
// On SpaceX a frame is thousands of draw calls, so a model sitting still
// on screen kept the GPU and a CPU core busy - the "heavy" the owner
// reported on 2026-09-17, visible as fans and a sluggish page long after
// loading. A still frame is identical to the one already on the canvas
// (the canvas keeps its pixels until the next draw), so it is skipped.
//
// A frame IS drawn when:
//   - the camera's world or projection matrix, or the canvas size,
//     changed since the last drawn frame (orbit, fly-to tween, spin,
//     resize) - and for `settleMs` after, so damping and late tile
//     installs land;
//   - streaming is busy (the caller draws on its own throttle then);
//   - the scene version moved (renderSignal: pools flushed, visibility,
//     colour, ghost) or requestRender() was called;
//   - any input happened on the page in the last `inputMs` - a click on
//     a panel button is how most scene changes start;
//   - `idleMs` passed since the last drawn frame: a safety net for any
//     change that reached the scene without saying so.
//
// Off: window.__renderOnDemand = false, or localStorage
// bl_render_on_demand=off.
export function renderOnDemandEnabled() {
  try {
    if (typeof window !== 'undefined') {
      if (window.__renderOnDemand === false) return false;
      if (typeof localStorage !== 'undefined' && localStorage.getItem('bl_render_on_demand') === 'off') return false;
    }
  } catch (e) { /* storage blocked: the default stands */ }
  return true;
}

export default class RenderOnDemand {
  constructor({ settleMs = 400, inputMs = 1000, idleMs = 500 } = {}) {
    this.settleMs = settleMs;
    this.inputMs = inputMs;
    this.idleMs = idleMs;
    this._sig = new Float64Array(34);
    this._primed = false;
    this._drawUntil = 0;
    this._lastDrawnAt = -Infinity;
    this._seenVersion = -1;
    this.skipped = 0;
    this.drawn = 0;
  }

  // Ask for frames for the next `ms` (0 = just the next one).
  request(now, ms = 0) {
    this._drawUntil = Math.max(this._drawUntil, now + Math.max(ms, 1));
  }

  // True when this frame must be drawn. `camera` needs matrixWorld and
  // projectionMatrix; width/height are the drawing buffer's.
  shouldDraw({ now, camera, width, height, sceneVersion, busy }) {
    // A camera or canvas change asks for frames for settleMs (damping and
    // late tile installs land); a SCENE change (pools flushed, a colour
    // written) asks for the next frame only - one draw shows it. Giving
    // every scene bump the 400 ms window too turned each small rest-pass
    // write into ~24 frames: Tom, real GPU, 6-18 draws/s with the camera
    // still (2026-10-13).
    let changed = !this._primed;
    let sceneOnly = false;
    const sig = this._sig;
    if (camera && camera.matrixWorld && camera.projectionMatrix) {
      const a = camera.matrixWorld.elements;
      const b = camera.projectionMatrix.elements;
      for (let i = 0; i < 16; i++) {
        if (sig[i] !== a[i]) { sig[i] = a[i]; changed = true; }
        if (sig[16 + i] !== b[i]) { sig[16 + i] = b[i]; changed = true; }
      }
    }
    if (sig[32] !== width || sig[33] !== height) { sig[32] = width; sig[33] = height; changed = true; }
    this._primed = true;
    if (sceneVersion !== this._seenVersion) { this._seenVersion = sceneVersion; sceneOnly = !changed; changed = true; }
    if (changed) { this.request(now, sceneOnly ? 0 : this.settleMs); if (sceneOnly) this.sceneWakes = (this.sceneWakes || 0) + 1; }
    const draw = busy || now < this._drawUntil || now - this._lastDrawnAt >= this.idleMs;
    if (draw) { this._lastDrawnAt = now; this.drawn += 1; } else { this.skipped += 1; }
    return draw;
  }
}
